/** The approval state a deliverable must be in to count as signed for. */
export const APPROVED_DELIVERABLE_STATUS = 'approved';

export type PreflightTask = {
	/** Relay global id of the task. */
	id: string;
	phase?: {id: string} | null;
};

export type PreflightAsset = {
	id: string;
	assetType: string;
	name: string;
	/** awaiting_approval | approved | changes_requested for a deliverable. */
	status: string;
	archived: boolean;
	/** Raw Forecast backend task id, or null for a project-level asset. */
	psaTaskId?: string | null;
};

export type OutstandingDeliverable = {id: string; name: string; status: string};

export type PhaseDeliverablePreflight =
	/** The phase has no deliverables, so there is nothing to pre-flight and no section renders. */
	| {kind: 'none'}
	/** Every deliverable in the phase is approved. */
	| {kind: 'all_approved'; total: number}
	/** At least one deliverable is not approved yet. Advisory only — it never blocks a send. */
	| {kind: 'outstanding'; total: number; outstanding: OutstandingDeliverable[]};

/**
 * The raw backend id inside a Relay global id (`base64("Task:123")` → `"123"`), which is
 * the id a workspace asset records as its `psaTaskId`. Null when the value is not a
 * decodable global id, so a malformed task simply matches nothing.
 */
export function rawIdFromGlobalId(globalId: string): string | null {
	try {
		const decoded = atob(globalId);
		const separator = decoded.indexOf(':');
		return separator > 0 && separator < decoded.length - 1 ? decoded.slice(separator + 1) : null;
	} catch {
		return null;
	}
}

/** A deliverable the client can still see — archived ones have been withdrawn from them. */
const isLiveDeliverable = (asset: PreflightAsset): boolean => asset.assetType === 'deliverable' && !asset.archived;

/**
 * The Relay global ids of the tasks that carry a live deliverable, each once. These are
 * the only tasks whose phase the pre-flight needs, which keeps that lookup bounded by the
 * project's deliverables rather than by its task count.
 */
export function deliverableTaskGlobalIds(assets: ReadonlyArray<PreflightAsset>): string[] {
	const rawIds = new Set<string>();
	for (const asset of assets) {
		if (isLiveDeliverable(asset) && asset.psaTaskId) rawIds.add(asset.psaTaskId);
	}
	return [...rawIds].map(rawId => btoa(`Task:${rawId}`));
}

/**
 * Which deliverables belong to `phaseId`, and whether any of them is still unapproved.
 *
 * A deliverable belongs to the phase when it is attached to one of the phase's tasks.
 * Project-level deliverables (no task) belong to no phase, and archived ones have been
 * withdrawn from the client, so neither is counted. Approval is the asset's own status:
 * a new version resets it to `awaiting_approval`, so a deliverable approved on an earlier
 * version correctly reads as outstanding again.
 */
export function derivePhaseDeliverablePreflight(
	phaseId: string,
	tasks: ReadonlyArray<PreflightTask>,
	assets: ReadonlyArray<PreflightAsset>
): PhaseDeliverablePreflight {
	const phaseTaskIds = new Set<string>();
	for (const task of tasks) {
		if (task.phase?.id !== phaseId) continue;
		const rawId = rawIdFromGlobalId(task.id);
		if (rawId) phaseTaskIds.add(rawId);
	}

	const deliverables = assets.filter(
		asset => isLiveDeliverable(asset) && !!asset.psaTaskId && phaseTaskIds.has(asset.psaTaskId)
	);
	if (deliverables.length === 0) return {kind: 'none'};

	const outstanding = deliverables
		.filter(asset => asset.status !== APPROVED_DELIVERABLE_STATUS)
		.map(({id, name, status}) => ({id, name, status}));
	return outstanding.length === 0
		? {kind: 'all_approved', total: deliverables.length}
		: {kind: 'outstanding', total: deliverables.length, outstanding};
}

/** Message id naming an outstanding deliverable together with the state it is in. */
export function outstandingDeliverableMessageId(status: string): string {
	switch (status) {
		case 'awaiting_approval':
			return 'send_signoff_modal.deliverables.item_awaiting_approval';
		case 'changes_requested':
			return 'send_signoff_modal.deliverables.item_changes_requested';
		default:
			return 'send_signoff_modal.deliverables.item_not_approved';
	}
}
